/**
 * IMPORTANT: The backend's export() function converts MongoDB _id fields to "id"
 * This means all API responses use "id" instead of "_id"
 *
 * These helpers ensure consistent handling across the app.
 * USE THESE EVERYWHERE you work with IDs from search results or API responses.
 */

/**
 * Extract ID from an object that might have either _id or id field
 * @param {object} obj - Object to extract ID from
 * @returns {string|null} - The ID or null
 */
export const getId = (obj) => {
  if (obj === null || obj === undefined) return null;

  if (typeof obj === "string" || typeof obj === "number") {
    return String(obj);
  }

  if (typeof obj !== "object") return null;

  const value = obj.id ?? obj._id;
  if (value === null || value === undefined) return null;

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (typeof value === "object" && typeof value.$oid === "string") {
    return value.$oid;
  }

  if (typeof value.toString === "function") {
    const converted = value.toString();
    return converted && converted !== "[object Object]" ? converted : null;
  }

  return null;
};

/**
 * Check if two objects have the same ID (handles both _id and id fields)
 * @param {object} obj1 - First object
 * @param {object} obj2 - Second object
 * @returns {boolean} - True if IDs match
 */
export const isSameId = (obj1, obj2) => {
  const id1 = getId(obj1);
  const id2 = getId(obj2);
  return !!id1 && !!id2 && id1 === id2;
};

/**
 * Compare ID to a value (handles both _id and id fields)
 * @param {object} obj - Object with ID
 * @param {string} value - ID value to compare
 * @returns {boolean} - True if IDs match
 */
export const idEquals = (obj, value) => {
  const id = getId(obj);
  const compareTo = getId(value);
  return !!id && !!compareTo && id === compareTo;
};
